import React, { useId, useState } from 'react';
import { splitsSummary, qtyText } from './LaCriteriaLegend';
import LaRowsPicker from './LaRowsPicker';
import NumberField from './NumberField';
import {
  MAX_LADDER, MAX_LADDER_AMOUNT, MAX_FLOOR_PCT, DEFAULT_LADDER_OFFSET, DEFAULT_LADDER_STEP,
  cleanLadderIds, cleanLadderAmount, cleanFloorPct, pctText,
} from '../../utils/lionAutoLadder';
import { cleanRowsInclude, rowsSummaryText } from '../../utils/lionAutoRows';
import { cleanOverrideIds } from '../../utils/lionAutoOverrides';
import { effectiveFloor, floorSourceText, floorUseText } from '../../utils/lionAutoFloors';
import {
  MAX_HOLD_PRICE, MAX_HOLD_SEATS, cleanHoldLive, cleanHoldPrice, cleanHoldSeats, holdOn, holdNeedsPrice, holdHintText,
} from '../../utils/lionAutoHold';
import { outlierRuleText, outliersDroppedText, positionRuleHint } from '../../utils/lionAutoOutliers';
import { zoneFallbackHint } from '../../utils/lionAutoListings';
import {
  MAX_NO_COMP_DROP_PCT, NEW_GROUP_NO_COMP_DROP_PCT, cleanNoCompPct, noCompHintText,
} from '../../utils/lionAutoNoComp';

// Criteria for one Lion Auto group (criteria JSON v2, the frozen contract).
// The basic controls are Compare's: the sections (picked on the map or a zone)
// and the Qty filter (a minimum, or "1 only": minQuantity and maxQuantity,
// 0 = no bound). The finer filters — the rows ticked from the rows that
// exist in the sections, Skybox Drive's splits and low outliers, our
// public-notes keyword filter and the zone fallback — sit under "Advanced
// filters", off for every group, a new one included: a pricer turns each on
// per group (the zone fallback, compZoneFallback: with no comp in its
// sections the comps come from the sections' Skybox zone, outside them).
// Rows, Qty and public notes apply to our listings and the market's alike,
// except Qty "1 only": our singles, compared with comps of any size.
// The server normalizes and resolves the criteria and the page shows what it
// resolved. The price ladder's keys ride along (saved with the group, ignored
// by matching): our listings in the order the pricer clicked them in the
// table, the $ below the anchor comp for rank 1 and the $ step between
// ranks, both in L, and the floor — a % of each ranked listing's own cost
// its B never goes under (0 = none of the group's own: the show's floor,
// else the artist's, when one is set) — and holding (holdLive / holdSeats /
// holdPrice, lionAutoHold: ranks 1..holdLive get the ladder price, and later
// ranks too until the live ones hold holdSeats seats (the Live qty, 0 = off),
// the rest one hold price, a B; 0 = off) — and the no-comp step-down (noCompDropPct,
// lionAutoNoComp: with no anchor comp the Auto runner lowers each ranked
// listing's B this % a day toward its floor; 0 = the price holds). The
// listings included or excluded
// by hand in the table (includeIds / excludeIds, lionAutoOverrides) and the
// market listings taken out of the comps by hand (excludeCompIds, Vivid ids)
// are matching keys: saved with the group, and a change re-matches.

// DEFAULT_CRITERIA is what a saved group missing a key reads as (and what the
// server fills in), so it stays put: plain sections, every row, every split,
// any public notes, no outlier cut, nothing included or excluded by hand
// (what Compare shows for the same sections), no zone fallback, nothing
// ranked, the ladder at $1 below the anchor comp, $2 per rank, with no floor,
// holding off and no step-down with no comp (the price holds).
// NEW_GROUP_CRITERIA below is what the New group form opens with.
export const DEFAULT_CRITERIA = {
  v: 2,
  sections: [],
  minQuantity: 0,
  // 0 = no max; "1 only" is min 1 + max 1
  maxQuantity: 0,
  // The picked row labels; empty = every row
  rowsInclude: [],
  splitMode: 'any',
  validSplits: null,
  notesMode: 'any',
  notesTerms: [],
  lowerOutlierMethod: 'none',
  lowerOutlierAmount: 0,
  lowerOutlierThreshold: 0,
  // Our listings in the group by hand / out of it by hand; never both
  includeIds: [],
  excludeIds: [],
  // Market listings out of the comps by hand, by Vivid listing id
  excludeCompIds: [],
  // The zone fallback (compMatchRules compZoneFallback): with no comp in the
  // sections, price off the sections' Skybox zone. A saved group without the
  // key reads as off, as on the server (its default), so a stored group
  // keeps the match it had; the New group form starts with it off too
  // (opt-in per group)
  compZoneFallback: false,
  ladderIds: [],
  ladderOffset: DEFAULT_LADDER_OFFSET,
  ladderStep: DEFAULT_LADDER_STEP,
  ladderFloorPct: 0,
  // Holding (lionAutoHold): ranks 1..holdLive get the ladder price, and
  // later ranks too until the live ones hold holdSeats seats (the Live qty,
  // 0 = off), the rest the hold price (a B); 0 = off
  holdLive: 0,
  holdSeats: 0,
  holdPrice: 0,
  // The no-comp step-down (lionAutoNoComp): % of B a day, 0 = off (the
  // server's default, so a stored group keeps holding its price)
  noCompDropPct: 0,
};

// The New group form's starting point: Qty 2+, the low-outlier rule off (as
// DEFAULT_CRITERIA; a pricer picks one per group), the zone fallback off
// (opt-in per group; the key is set so the pin stays explicit) and the
// no-comp step-down at 5% a day
// (NEW_GROUP_NO_COMP_DROP_PCT), as the groups a Lion Checker import
// creates (importRules IMPORT_DEFAULTS), rank 1 at $2 below the anchor comp,
// $5 per rank. Only the form uses it; saved groups keep their values.
const NEW_GROUP_MIN_QTY = 2;
const NEW_GROUP_LADDER_OFFSET = 2;
const NEW_GROUP_LADDER_STEP = 5;
export const NEW_GROUP_CRITERIA = {
  ...DEFAULT_CRITERIA,
  minQuantity: NEW_GROUP_MIN_QTY,
  compZoneFallback: false,
  ladderOffset: NEW_GROUP_LADDER_OFFSET,
  ladderStep: NEW_GROUP_LADDER_STEP,
  noCompDropPct: NEW_GROUP_NO_COMP_DROP_PCT,
};

export const MAX_SECTIONS = 500;
const MAX_NOTES_TERMS = 20;
const MAX_NOTES_TERM_LENGTH = 60;
// any = notes don't filter; include = a listing's public notes must contain
// one of the terms; exclude = they must contain none of them
const NOTES_MODES = ['any', 'include', 'exclude'];
// The server's bound for minQuantity and maxQuantity alike
const MAX_MIN_QTY = 10;
const SPLIT_BOXES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
const SPLIT_MODES = ['any', 'ladder', 'custom'];
// Compare's Qty filter plus a ceiling: Any, 1 only, 2+ … 6+, each a
// (minQuantity, maxQuantity) pair. Any is 0/0, "1 only" is 1/1, "n+" is n/0.
// Sorted by min (where a saved odd pair slots in).
const QTY_OPTIONS = [
  { min: 0, max: 0 },
  { min: 1, max: 1 },
  { min: 2, max: 0 },
  { min: 3, max: 0 },
  { min: 4, max: 0 },
  { min: 5, max: 0 },
  { min: 6, max: 0 },
];
const qtyKey = (o) => `${o.min}:${o.max}`;
// The ladder's usual steps between ranks, one click each
const STEP_PRESETS = [2, 5, 10];
// The usual floors, % of cost, one click each (Drive's org default on this
// account is 130)
const FLOOR_PRESETS = [100, 110, 120, 130];

// Drive's labels. Amount/threshold start values are only form defaults so a
// newly picked method does something visible; the pricer edits them. "%
// threshold of position" starts at 25% under the 3rd cheapest comp (with
// fewer than 3 comps none is ignored), so one pick keeps a lone cheap comp
// from anchoring the ladder; "$ threshold of position" starts at the same
// position 3 (position 1 would drop nothing under Drive's rule).
const POSITION_RULE_AMOUNT = 3;
const POSITION_RULE_PCT = 25;
export const OUTLIER_METHODS = [
  { value: 'none', label: 'Off', amount: 0, threshold: 0 },
  { value: 'quantity', label: 'Quantity of listings', amount: 1, threshold: 0 },
  { value: 'currency', label: 'Below dollar amount', amount: 0, threshold: 0 },
  { value: 'percentage', label: 'Percentage of listings', amount: 10, threshold: 0 },
  {
    value: 'percentThresholdOfPosition',
    label: '% threshold of position',
    amount: POSITION_RULE_AMOUNT,
    threshold: POSITION_RULE_PCT,
  },
  { value: 'currencyThresholdOfPosition', label: '$ threshold of position', amount: 3, threshold: 10 },
];

// Saved criteria may miss keys (older server) or carry ones that are gone
// (v1's sectionSelectionMode, the old row window and row ranges): keep the
// current keys only and fill in the rest. A group saved before the max
// existed reads as no max; a max under the min is dropped (the server
// rejects that pair).
export function withCriteriaDefaults(criteria) {
  const src = criteria || {};
  const c = {};
  for (const key of Object.keys(DEFAULT_CRITERIA)) {
    c[key] = src[key] === undefined ? DEFAULT_CRITERIA[key] : src[key];
  }
  const qtyBound = (v) => {
    const n = Number(v);
    return Number.isInteger(n) && n >= 0 && n <= MAX_MIN_QTY ? n : 0;
  };
  const minQuantity = qtyBound(c.minQuantity);
  const maxQ = qtyBound(c.maxQuantity);
  return {
    ...c,
    v: 2,
    sections: Array.isArray(c.sections) ? c.sections : [],
    minQuantity,
    maxQuantity: maxQ > 0 && maxQ < minQuantity ? 0 : maxQ,
    rowsInclude: cleanRowsInclude(c.rowsInclude),
    // A v1 group's own split list had no mode: that list is a custom one
    splitMode: SPLIT_MODES.includes(src.splitMode) ? src.splitMode : (c.validSplits ? 'custom' : 'any'),
    notesMode: NOTES_MODES.includes(c.notesMode) ? c.notesMode : 'any',
    notesTerms: Array.isArray(c.notesTerms) ? c.notesTerms.filter(t => typeof t === 'string') : [],
    includeIds: cleanOverrideIds(c.includeIds),
    excludeIds: cleanOverrideIds(c.excludeIds),
    excludeCompIds: cleanOverrideIds(c.excludeCompIds),
    // A boolean only, as the server takes it: anything but true is off
    compZoneFallback: c.compZoneFallback === true,
    ladderIds: cleanLadderIds(c.ladderIds),
    ladderOffset: cleanLadderAmount(c.ladderOffset, DEFAULT_LADDER_OFFSET),
    ladderStep: cleanLadderAmount(c.ladderStep, DEFAULT_LADDER_STEP),
    ladderFloorPct: cleanFloorPct(c.ladderFloorPct),
    holdLive: cleanHoldLive(c.holdLive),
    holdSeats: cleanHoldSeats(c.holdSeats),
    holdPrice: cleanHoldPrice(c.holdPrice),
    noCompDropPct: cleanNoCompPct(c.noCompDropPct),
  };
}

// Duplicate's starting criteria: the saved group's, unranked, with none of
// our listings included or excluded by hand (those were picks among the
// original's listings, and a listing stays with the group that holds it).
// The comps taken out by hand stay (excludeCompIds): comps are shared, not
// held by one group.
export function duplicateCriteria(criteria) {
  return { ...withCriteriaDefaults(criteria), ladderIds: [], includeIds: [], excludeIds: [] };
}

// Public-notes terms as the server takes them: whitespace collapsed and
// trimmed, at most 60 characters, blanks dropped, case-insensitive
// duplicates dropped (the first spelling stays), at most 20
export function cleanNotesTerms(terms) {
  const out = [];
  const seen = new Set();
  for (const t of Array.isArray(terms) ? terms : []) {
    if (typeof t !== 'string') continue;
    const term = t.replace(/\s+/g, ' ').trim().slice(0, MAX_NOTES_TERM_LENGTH).trim();
    const key = term.toLowerCase();
    if (!term || seen.has(key)) continue;
    seen.add(key);
    out.push(term);
    if (out.length >= MAX_NOTES_TERMS) break;
  }
  return out;
}

// What goes over the wire. An empty custom split list stays in the form but
// is not sent (the server would reject it), so the live preview keeps
// working while the pricer ticks. The split list only travels in custom mode
// and the notes terms only with a notes mode on, so flipping modes back and
// forth costs no match. The picked rows go as withCriteriaDefaults cleaned
// them (natural order, so the same set always reads the same), the ids
// included / excluded by hand too, and the ladder keys (the preview strips
// those again).
export function cleanCriteria(criteria) {
  const c = withCriteriaDefaults(criteria);
  const splits = Array.isArray(c.validSplits?.splits) ? c.validSplits.splits : [];
  const customOk = c.splitMode === 'custom' && splits.length > 0;
  return {
    ...c,
    sections: c.sections.map(s => ({
      sectionId: Number(s.sectionId),
      name: String(s.name || `Section ${s.sectionId}`).slice(0, 80),
    })),
    splitMode: c.splitMode === 'custom' && !customOk ? 'any' : c.splitMode,
    validSplits: customOk ? { splits, includeMax: !!c.validSplits.includeMax } : null,
    notesTerms: c.notesMode === 'any' ? [] : cleanNotesTerms(c.notesTerms),
  };
}

// One line for the collapsed "Advanced filters" toggle: what is on, or "off"
export function advancedSummary(criteria) {
  const c = withCriteriaDefaults(criteria);
  const parts = [];
  const rows = rowsSummaryText(c.rowsInclude);
  if (rows) parts.push(rows);
  if (c.splitMode === 'ladder') parts.push('Splits ladder');
  if (c.splitMode === 'custom') {
    const splits = c.validSplits?.splits || [];
    parts.push(splits.length
      ? `Splits ${splits.join(', ')}${c.validSplits.includeMax ? ' + full' : ''}`
      : 'Splits: none ticked');
  }
  if (c.notesMode !== 'any') {
    const terms = cleanNotesTerms(c.notesTerms);
    parts.push(terms.length
      ? `Notes ${c.notesMode === 'include' ? 'contain' : 'exclude'}: ${terms.join(', ')}`
      : 'Notes: no words yet');
  }
  const outlierRule = outlierRuleText(c.lowerOutlierMethod, c.lowerOutlierAmount, c.lowerOutlierThreshold);
  if (outlierRule) parts.push(outlierRule);
  if (c.compZoneFallback) parts.push('Zone fallback');
  // Taken out in the table (Not a comp), not here: the line still says so
  const outComps = c.excludeCompIds.length;
  if (outComps) parts.push(`${outComps} comp${outComps === 1 ? '' : 's'} taken out by hand`);
  return parts.length ? parts.join(' · ') : 'off';
}

// The public-notes words as chips with a box to type the next one. Enter or
// a comma adds what is typed (a pasted "a, b" adds both), leaving the box
// adds what is left in it; × removes a chip, Backspace in the empty box the
// last one. Duplicates (any case) are not added twice.
function TermsInput({ terms, onChange }) {
  const [text, setText] = useState('');
  const full = terms.length >= MAX_NOTES_TERMS;
  const add = (list) => {
    const next = cleanNotesTerms([...terms, ...list]);
    if (next.length !== terms.length || next.some((t, i) => t !== terms[i])) onChange(next);
  };
  const commit = () => {
    if (text.trim()) add([text]);
    setText('');
  };
  return (
    <div className="la-terms">
      {terms.map((t, i) => (
        <span key={`${i}:${t}`} className="la-chip">
          {t}
          <button type="button" title="Remove this word" aria-label={`Remove ${t}`} onClick={() => onChange(terms.filter((_, j) => j !== i))}>&times;</button>
        </span>
      ))}
      <input
        className="form-input form-input-sm"
        value={text}
        maxLength={MAX_NOTES_TERM_LENGTH}
        disabled={full}
        placeholder={full ? `${MAX_NOTES_TERMS} words at most` : 'Type a word or phrase, then Enter'}
        aria-label="Add a word or phrase"
        onChange={(e) => {
          const v = e.target.value;
          if (!v.includes(',')) { setText(v); return; }
          const parts = v.split(',');
          add(parts.slice(0, -1));
          setText(parts[parts.length - 1].trimStart());
        }}
        onKeyDown={(e) => {
          if (e.nativeEvent.isComposing) return;
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          } else if (e.key === 'Backspace' && text === '' && terms.length > 0) {
            onChange(terms.slice(0, -1));
          }
        }}
        onBlur={commit}
      />
    </div>
  );
}

// chartSections: the seat map's sections by id (null until the map loads), to
// flag a chosen section the map doesn't have; it still counts and is sent.
// anchorListing: our cheapest listing in the sections, from the live preview.
// rowOptions: the rows of ours and the market's in the draft's sections
// (rowOptionsFrom), null while the listings load.
// initialAdvancedOpen: start with the advanced filters open (a duplicate).
// inheritedFloor: the floor a blank one uses ({ pct, source: 'show' |
// 'artist' | null, performerName }, inheritedFloorOf), null while unknown.
function LaCriteriaEditor({
  criteria, onChange, resolved, anchorListing, chartSections, rowOptions = null, initialAdvancedOpen = false,
  inheritedFloor = null,
}) {
  // Collapsed when the editor opens (it is keyed per group), even when the
  // saved group has filters on: the summary line says which. A duplicate
  // opens them, since changing a filter is what tells it apart.
  const [advancedOpen, setAdvancedOpen] = useState(initialAdvancedOpen);
  // Ties the outlier method select to its rule hint (aria-describedby)
  const hintId = useId();
  // Ties the zone fallback checkbox to its hint the same way
  const zoneHintId = useId();
  // And the no-comp step-down's box (NumberField ariaDescribedBy)
  const noCompHintId = useId();
  const c = criteria;
  const set = (patch) => onChange({ ...c, ...patch });

  // ---- sections ----
  const removeSection = (id) => set({ sections: c.sections.filter(s => s.sectionId !== id) });
  const offMap = (s) => !!chartSections && !chartSections[String(s.sectionId)];

  // ---- qty ----
  // The select's value is the (min, max) pair. A saved pair the list doesn't
  // offer (a 1+ from before "1 only", a 7+ … 10+, a range) still shows as
  // itself, slotted in by its min
  const qty = { min: Number(c.minQuantity) || 0, max: Number(c.maxQuantity) || 0 };
  const qtyKnown = QTY_OPTIONS.some(o => qtyKey(o) === qtyKey(qty));
  const qtyAt = QTY_OPTIONS.filter(o => o.min <= qty.min).length;
  const qtyOptions = qtyKnown ? QTY_OPTIONS : [...QTY_OPTIONS.slice(0, qtyAt), qty, ...QTY_OPTIONS.slice(qtyAt)];
  const pickQty = (key) => {
    const o = qtyOptions.find(x => qtyKey(x) === key);
    if (o) set({ minQuantity: o.min, maxQuantity: o.max });
  };

  // ---- rows ----
  const rowsInclude = Array.isArray(c.rowsInclude) ? c.rowsInclude : [];

  // ---- splits ----
  const splits = Array.isArray(c.validSplits?.splits) ? c.validSplits.splits : [];
  const splitBoxes = [...new Set([...SPLIT_BOXES, ...splits])].sort((a, b) => a - b);
  // Custom keeps a list made earlier in this edit; otherwise it starts from
  // the ladder the server resolved, else our cheapest listing's quantity
  const startCustomSplits = () => {
    if (splits.length) { set({ splitMode: 'custom' }); return; }
    const ladder = resolved?.validSplits?.source === 'ladder' ? (resolved.validSplits.splits || []) : [];
    const q = Number(anchorListing?.quantity);
    const seed = ladder.length ? ladder : (Number.isInteger(q) && q >= 1 && q <= 30 ? [q] : [2]);
    set({
      splitMode: 'custom',
      validSplits: {
        splits: seed.filter(n => Number.isInteger(n) && n >= 1 && n <= 30).slice(0, 20),
        includeMax: resolved?.validSplits?.includeMax ?? true,
      },
    });
  };
  const toggleSplit = (n) => {
    const next = splits.includes(n) ? splits.filter(x => x !== n) : [...splits, n].sort((a, b) => a - b);
    set({ validSplits: { includeMax: true, ...c.validSplits, splits: next.slice(0, 20) } });
  };
  const ladderResolved = resolved?.validSplits?.source === 'ladder' && (resolved.validSplits.splits || []).length > 0;

  // ---- public notes ----
  // The terms stay in the form while the mode is "Any notes" (not sent), so
  // switching it back on brings them back
  const notesMode = NOTES_MODES.includes(c.notesMode) ? c.notesMode : 'any';
  const notesTerms = Array.isArray(c.notesTerms) ? c.notesTerms : [];
  const noNotesTerms = cleanNotesTerms(notesTerms).length === 0;

  // ---- outliers ----
  const method = OUTLIER_METHODS.find(m => m.value === c.lowerOutlierMethod) || OUTLIER_METHODS[0];
  const pickMethod = (value) => {
    const m = OUTLIER_METHODS.find(x => x.value === value) || OUTLIER_METHODS[0];
    set({ lowerOutlierMethod: m.value, lowerOutlierAmount: m.amount, lowerOutlierThreshold: m.threshold });
  };
  const amountField = (props) => (
    <NumberField value={c.lowerOutlierAmount} onChange={(v) => set({ lowerOutlierAmount: v })} {...props} />
  );
  const thresholdField = (props) => (
    <NumberField value={c.lowerOutlierThreshold} onChange={(v) => set({ lowerOutlierThreshold: v })} {...props} />
  );
  // What a position rule does, in words (null for the other methods)
  const ruleHint = positionRuleHint(method.value, c.lowerOutlierAmount, c.lowerOutlierThreshold);

  // ---- zone fallback ----
  // The hint reads the live preview (resolved): whether the zone prices the
  // group now, or the sections have no zone
  const zoneOn = c.compZoneFallback === true;
  const zoneHint = zoneFallbackHint(resolved, zoneOn);

  // ---- floor ----
  // With a show or artist floor set, blank (0) uses it: the box, its hint
  // and the clear button say which (the box is wider for the placeholder)
  const inherit = effectiveFloor(0, inheritedFloor);
  const inheritName = inheritedFloor?.performerName ?? null;
  const inheritText = inherit.pct > 0 ? `${floorUseText(inherit.source, inheritName)} (${pctText(inherit.pct)})` : null;
  let clearFloorTitle = 'Turn the floor off';
  if (inheritText) clearFloorTitle = c.ladderFloorPct ? `Drop this group's own floor: use ${inheritText}` : `This group already uses ${inheritText}`;

  // ---- holding ----
  // On sets 1 live listing; off keeps the hold price and the Live qty for
  // turning it back on
  const holding = holdOn(c);
  const needsHoldPrice = holdNeedsPrice(c);
  const toggleHolding = (on) => set(on ? { holdLive: 1 } : { holdLive: 0 });

  const summary = advancedSummary(c);
  const ranked = Array.isArray(c.ladderIds) ? c.ladderIds.length : 0;
  // ---- comps taken out by hand ----
  // Taken out (and put back) per row in the table; one that sold or left the
  // sections since has no row there, so Clear puts them all back
  const outComps = Array.isArray(c.excludeCompIds) ? c.excludeCompIds.length : 0;

  return (
    <div className="la-criteria">
      <div className="la-basic-row">
        <fieldset className="la-field la-sections-field">
          <legend>Sections <span className="la-muted">{c.sections.length}</span></legend>
          {c.sections.length > 0 && (
            <div className="la-chips">
              {c.sections.map(s => {
                const off = offMap(s);
                return (
                  <span
                    key={s.sectionId}
                    className={`la-chip${off ? ' la-chip-offmap' : ''}`}
                    title={off ? 'This section is not on this seat map; it stays in the group' : undefined}
                  >
                    {s.name || `#${s.sectionId}`}
                    {off && <span className="la-chip-note">not on this map</span>}
                    <button type="button" title="Remove this section" onClick={() => removeSection(s.sectionId)}>&times;</button>
                  </span>
                );
              })}
            </div>
          )}
          <div className="la-hint">
            {c.sections.length
              ? 'Click the map or a zone to add or remove sections. Your listings in them are the group.'
              : 'Click the map or a zone to pick sections. Your listings in them join the group by themselves.'}
          </div>
        </fieldset>

        <fieldset className="la-field la-qty-field">
          <legend>Qty</legend>
          <select
            className="form-input form-input-sm"
            value={qtyKey(qty)}
            onChange={(e) => pickQty(e.target.value)}
            title={'By how many tickets a listing has, for your listings and the market alike. '
              + '"2+" leaves out listings with fewer tickets; "1 only" keeps your single-ticket listings '
              + 'and compares them with comps of any size.'}
          >
            {qtyOptions.map(o => <option key={qtyKey(o)} value={qtyKey(o)}>{qtyText(o.min, o.max)}</option>)}
          </select>
          <div className="la-hint">Your listings and the market. &quot;1 only&quot; = your singles; comps of any size.</div>
        </fieldset>

        <fieldset className="la-field">
          <legend>Price ladder</legend>
          <div className="la-inline">
            <label title="Rank 1's L sits this far below the anchor comp's L (the cheapest matching market listing)">
              <NumberField value={c.ladderOffset} min={0} max={MAX_LADDER_AMOUNT} onChange={(v) => set({ ladderOffset: v })} />
              $ below the anchor comp
            </label>
          </div>
          <div className="la-inline">
            <span title="Each next rank's L is at least this much above the rank before it">Step</span>
            {STEP_PRESETS.map(n => (
              <button
                key={n}
                type="button"
                className={`btn btn-sm ${c.ladderStep === n ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => set({ ladderStep: n })}
              >
                ${n}
              </button>
            ))}
            <NumberField value={c.ladderStep} min={0} max={MAX_LADDER_AMOUNT} prefix="$" title="Any step, cents allowed" onChange={(v) => set({ ladderStep: v })} />
          </div>
          <div className="la-inline">
            <span title="No ranked listing is priced under its own cost × this %">Floor</span>
            <span className={inheritText ? 'la-floor-inherit' : undefined}>
              <NumberField
                value={c.ladderFloorPct}
                min={0}
                max={MAX_FLOOR_PCT}
                blankValue={0}
                placeholder={inheritText ? `${pctText(inherit.pct)} · ${floorSourceText(inherit.source, inheritName)}` : 'none'}
                suffix="% of cost"
                ariaLabel="Floor, % of cost"
                title={`Any % up to ${MAX_FLOOR_PCT}, cents allowed; blank = ${inheritText ? `use ${inheritText}` : 'no floor'}`}
                onChange={(v) => set({ ladderFloorPct: v })}
              />
            </span>
            {FLOOR_PRESETS.map(n => (
              <button
                key={n}
                type="button"
                className={`btn btn-sm ${c.ladderFloorPct === n ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => set({ ladderFloorPct: n })}
              >
                {n}%
              </button>
            ))}
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              disabled={!c.ladderFloorPct}
              title={clearFloorTitle}
              onClick={() => set({ ladderFloorPct: 0 })}
            >
              {inheritText ? `Use ${floorUseText(inherit.source, inheritName)}` : 'Clear floor'}
            </button>
          </div>
          <div className="la-hint">
            Never price a ladder listing below its cost × this %. A listing priced at its floor stays there and the
            listings above it step up from it. {inheritText ? `Blank = use ${inheritText}.` : 'Blank = no floor.'}
          </div>
          <div className="la-inline">
            <label className="la-check" title="Keep only the first ranks at the ladder price and park the rest at one hold price (B), for General Admission">
              <input type="checkbox" checked={holding} onChange={(e) => toggleHolding(e.target.checked)} /> Holding
            </label>
            {holding && (
              <>
                <label title={`How many ranks get the ladder price: ranks 1 to this, ${MAX_LADDER} at most`}>
                  Live listings
                  <NumberField
                    value={c.holdLive}
                    min={1}
                    max={MAX_LADDER}
                    integer
                    ariaLabel="Live listings"
                    onChange={(v) => set({ holdLive: v })}
                  />
                </label>
                <label title="At least this many seats live at the get-in: later ranks go live until the live listings hold this many seats; 0 = off">
                  Live qty
                  <NumberField
                    value={c.holdSeats}
                    min={0}
                    max={MAX_HOLD_SEATS}
                    integer
                    blankValue={0}
                    suffix="seats"
                    placeholder="any"
                    ariaLabel="Live qty, seats"
                    onChange={(v) => set({ holdSeats: v })}
                  />
                </label>
                <label title={`The B every later rank sits at (never under its floor); up to $${MAX_HOLD_PRICE.toLocaleString('en-US')}, cents allowed`}>
                  Hold price
                  <NumberField
                    value={c.holdPrice}
                    min={0}
                    max={MAX_HOLD_PRICE}
                    blankValue={0}
                    prefix="$"
                    placeholder="none"
                    ariaLabel="Hold price, B"
                    onChange={(v) => set({ holdPrice: v })}
                  />
                </label>
              </>
            )}
          </div>
          {holding && (
            <div className={`la-hint${needsHoldPrice ? ' warn' : ''}`}>
              {holdHintText(c.holdLive, c.holdSeats)}{needsHoldPrice && ' Set a hold price above $0.'}
            </div>
          )}
          <div className="la-inline">
            <label title={`With no market listing to price against, Auto lowers each ranked listing's B this % a day toward its floor; up to ${MAX_NO_COMP_DROP_PCT}%, cents allowed`}>
              No comps at all: drop
              <NumberField
                value={c.noCompDropPct}
                min={0}
                max={MAX_NO_COMP_DROP_PCT}
                blankValue={0}
                suffix="%"
                placeholder="0"
                ariaLabel="No comps: drop, % a day"
                ariaDescribedBy={noCompHintId}
                onChange={(v) => set({ noCompDropPct: v })}
              />
              a day toward the floor (0 = hold the price)
            </label>
          </div>
          <div className="la-hint" id={noCompHintId}>{noCompHintText(c.noCompDropPct)}</div>
          <div className="la-inline">
            <span>{ranked} ranked</span>
            <button type="button" className="btn btn-secondary btn-sm" disabled={!ranked} onClick={() => set({ ladderIds: [] })}>Clear order</button>
          </div>
          <div className={`la-hint${ranked >= MAX_LADDER ? ' warn' : ''}`}>
            Click our listings in the table in the order you want: 1 = lowest price.
            {ranked >= MAX_LADDER && ` ${MAX_LADDER} at most.`}
          </div>
        </fieldset>
      </div>

      <div className={`la-advanced${advancedOpen ? ' open' : ''}`}>
        <button
          type="button"
          className="la-advanced-toggle"
          aria-expanded={advancedOpen}
          onClick={() => setAdvancedOpen(o => !o)}
          title={advancedOpen ? 'Hide the advanced filters' : 'Rows, splits, public notes and low-outlier filters, and the zone fallback'}
        >
          <span className="la-advanced-caret">{advancedOpen ? '▾' : '▸'}</span>
          Advanced filters
          <span className={`la-advanced-summary${summary === 'off' ? '' : ' on'}`}>{summary}</span>
        </button>

        {advancedOpen && (
          <div className="la-advanced-grid">
            <fieldset className="la-field">
              <legend>Rows</legend>
              <div className="la-inline">
                <LaRowsPicker
                  picked={rowsInclude}
                  options={rowOptions}
                  hasSections={c.sections.length > 0}
                  onChange={(next) => set({ rowsInclude: next })}
                />
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  disabled={rowsInclude.length === 0}
                  title="Untick every row: every row matches again"
                  onClick={() => set({ rowsInclude: [] })}
                >
                  Clear rows
                </button>
              </div>
              <div className="la-hint">
                Only the ticked rows match, for your listings and the market&apos;s. Nothing ticked = every row.
                Listings with no row label only match when nothing is ticked.
              </div>
            </fieldset>

            <fieldset className="la-field">
              <legend>Splits</legend>
              <div className="la-inline">
                <label><input type="radio" checked={c.splitMode === 'any'} onChange={() => set({ splitMode: 'any' })} /> Any split</label>
                <label><input type="radio" checked={c.splitMode === 'ladder'} onChange={() => set({ splitMode: 'ladder' })} /> Ladder from your cheapest listing's quantity</label>
                <label><input type="radio" checked={c.splitMode === 'custom'} onChange={startCustomSplits} /> Custom</label>
              </div>
              <div className="la-hint">
                Splits match by what a listing can be sold in. To group by ticket count (1 only, 2+), use Qty.
              </div>
              {c.splitMode === 'custom' && (
                <>
                  <div className="la-inline la-split-boxes">
                    {splitBoxes.map(n => (
                      <label key={n}><input type="checkbox" checked={splits.includes(n)} onChange={() => toggleSplit(n)} /> {n}</label>
                    ))}
                  </div>
                  <label className="la-check">
                    <input
                      type="checkbox"
                      checked={!!c.validSplits?.includeMax}
                      onChange={(e) => set({ validSplits: { ...c.validSplits, splits, includeMax: e.target.checked } })}
                    /> Also match a comp's full quantity
                  </label>
                  {splits.length === 0 && <div className="la-hint warn">Tick at least one split; until then every split matches.</div>}
                </>
              )}
              {c.splitMode === 'ladder' && (
                <div className="la-hint">
                  {ladderResolved
                    ? `Your cheapest listing's qty ${anchorListing?.quantity ?? '?'}: ${splitsSummary(resolved.validSplits)}`
                    : anchorListing
                      ? "Comps must sell in a split your cheapest listing's quantity allows (Drive ladder)."
                      : "Drive ladder from your cheapest listing's quantity. With no listing of yours in these sections, every split matches."}
                </div>
              )}
              {c.splitMode === 'any' && <div className="la-hint">Comps in any split match.</div>}
            </fieldset>

            <fieldset className="la-field">
              <legend>Public notes</legend>
              <div className="la-inline">
                <label><input type="radio" checked={notesMode === 'any'} onChange={() => set({ notesMode: 'any' })} /> Any notes</label>
                <label><input type="radio" checked={notesMode === 'include'} onChange={() => set({ notesMode: 'include' })} /> Must contain any of</label>
                <label><input type="radio" checked={notesMode === 'exclude'} onChange={() => set({ notesMode: 'exclude' })} /> Must not contain</label>
              </div>
              {notesMode === 'any' ? (
                <div className="la-hint">Listings match whatever their public notes say.</div>
              ) : (
                <>
                  <TermsInput terms={notesTerms} onChange={(next) => set({ notesTerms: next })} />
                  <div className={`la-hint${noNotesTerms ? ' warn' : ''}`}>
                    Reads the public notes of your listings and the competitors&apos;, case-insensitive.{' '}
                    {notesMode === 'include'
                      ? "Only listings whose notes contain one of these words match; blank notes don't."
                      : 'Listings whose notes contain any of these words are left out; blank notes stay.'}{' '}
                    An empty list filters nothing.
                  </div>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => set({ notesMode: 'any', notesTerms: [] })}>Clear filter</button>
                </>
              )}
            </fieldset>

            <fieldset className="la-field">
              <legend>Exclude lowest listings</legend>
              <select
                className="form-input form-input-sm"
                value={method.value}
                onChange={(e) => pickMethod(e.target.value)}
                aria-label="Exclude lowest listings"
                aria-describedby={ruleHint ? hintId : undefined}
              >
                {OUTLIER_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
              {ruleHint && <div className="la-hint" id={hintId}>{ruleHint}</div>}
              {method.value === 'quantity' && (
                <div className="la-inline"><label>Drop the cheapest {amountField({ integer: true })} comps</label></div>
              )}
              {method.value === 'currency' && (
                <div className="la-inline"><label>Drop comps below {amountField({ prefix: '$' })} L</label></div>
              )}
              {method.value === 'percentage' && (
                <div className="la-inline"><label>Drop the cheapest {amountField({ max: 100, suffix: '%' })} of comps</label></div>
              )}
              {(method.value === 'percentThresholdOfPosition' || method.value === 'currencyThresholdOfPosition') && (
                <div className="la-inline">
                  <label>Measure from the comp at position {amountField({ integer: true })};</label>
                  <label>
                    drop comps more than{' '}
                    {method.value === 'percentThresholdOfPosition' ? thresholdField({ suffix: '%' }) : thresholdField({ prefix: '$' })}
                    {' '}under it
                  </label>
                </div>
              )}
              {resolved?.outlier && resolved.outlier.method !== 'none' && (
                <div className="la-hint">{outliersDroppedText(resolved.outlier.dropped) ?? 'No low outliers dropped'}.</div>
              )}
            </fieldset>

            <fieldset className="la-field">
              <legend>No comp in these sections</legend>
              <label className="la-check">
                <input
                  type="checkbox"
                  checked={zoneOn}
                  onChange={(e) => set({ compZoneFallback: e.target.checked })}
                  aria-describedby={zoneHintId}
                /> When these sections have no comp, use the zone&apos;s comps
              </label>
              <div className={`la-hint${zoneHint.warn ? ' warn' : ''}`} id={zoneHintId}>{zoneHint.text}</div>
            </fieldset>

            {outComps > 0 && (
              <fieldset className="la-field">
                <legend>Comps taken out by hand <span className="la-muted">{outComps}</span></legend>
                <div className="la-hint">
                  Taken out with Not a comp in the table. One that sold or left these sections has no row there to put it back from.
                </div>
                <button
                  type="button"
                  className="la-link"
                  title="Use every market listing as a comp again (undo every Not a comp)"
                  onClick={() => set({ excludeCompIds: [] })}
                >
                  Clear
                </button>
              </fieldset>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default LaCriteriaEditor;
